import React, { useRef, useState } from "react";
import { Download, Upload, X, AlertTriangle } from "lucide-react";
import { toast } from "react-hot-toast";
import { useLanguage } from "../../contexts/LanguageContext";
import { useModal } from "../../hooks/useModal";
import { widgetConfigApi } from "../../services/widgetConfigApi";

// Canonical value lives in ai-agent-backend:
// src/app/schemas/widget_config.py (MAX_PAGE_CONTEXTS). That is what actually
// refuses an oversized list, on the import AND on the save; this copy only
// drives the counter, so drift shows a wrong number and lets nothing through.
// Same arrangement, and the same caveat, as FEATURE_DEFAULTS in
// WidgetSettingsPage: if drift becomes a real risk, have the backend publish it.
const MAX_PAGE_CONTEXTS = 10_000;

// Canonical value lives in ai-agent-backend:
// src/app/api/widget_config_endpoints.py (MAX_IMPORT_BODY_BYTES), 8 MB since
// RPAA-247. Keep this equal to it, never below: a lower value here silently
// becomes the effective limit, and our own export (indented JSON, ~5.3 MB at
// the scale RPAA-247 supports) could no longer be imported back.
const MAX_FILE_BYTES = 8 * 1024 * 1024;

// btn-secondary, one size down. These two sit inside a field rather than in a
// page toolbar, so they should read as secondary to the section heading they
// share a row with, not compete with it. The utilities win over the class
// without !important because Tailwind's utilities layer comes after components.
const IO_BUTTON = "btn-secondary text-xs px-3 py-1.5 gap-1.5";

/**
 * Reescreve as posições que o backend usa para descrever um ficheiro recusado.
 *
 * Ele escreve `pageContexts[3].urlPatterns: Field required`, 0-based e com o
 * caminho do campo colado (page_contexts_io._describe). O que a string descreve
 * é o FICHEIRO, e é isso que ela passa a dizer por extenso, porque um import
 * recusado não escreve nada: o validate_import levanta o PageContextImportError
 * antes de qualquer escrita, portanto a lista guardada fica exactamente como
 * estava. Nomear cartões do editor mandava o operador abrir um cartão da lista
 * ANTIGA, sem relação nenhuma com a entrada do ficheiro que ele tem de corrigir.
 *
 * Fora do componente porque não fecha sobre nada: recebe o texto e o translate,
 * e assim não se reconstrói a cada tecla escrita nos campos de copy acima.
 */
const describeFileEntries = (detail, translate) =>
  detail.replace(/pageContexts\[(\d+)\](\.[\w.]+)?/g, (_, index, field) => {
    const entry = translate("pageContextsFileEntry", { index: Number(index) + 1 });
    return field ? `${entry} (${field.slice(1)})` : entry;
  });

/**
 * Export and import of this chat's page contexts.
 *
 * Export is always available — it destroys nothing — and exports what is SAVED,
 * not what is on screen. Import is blocked while the form is dirty: it writes on
 * the server, outside the form, so letting it run with unsaved edits either
 * loses them on the refetch or, worse, lets the next Save overwrite what was
 * just imported.
 */
const PageContextsIO = ({ label, chatId, contextCount, hasUnsavedChanges, onImported }) => {
  const { translate } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState(null); // { file, incomingCount, replacesQuickReplies, names }
  // { items, names }: os avisos do servidor e os nomes dos contextos do ficheiro,
  // pela ordem do ficheiro, que é a dos índices dos avisos.
  const [warnings, setWarnings] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);
  const inputRef = useRef(null);

  const close = () => {
    if (uploading) return;
    setIsOpen(false);
    setPending(null);
    setIsDragOver(false);
  };
  const modalRef = useModal(isOpen, close);

  // `contextCount` é null até o PageContextsBrowser ler a lista pela primeira
  // vez: o GET das definições já não traz os contextos. Sem o número, a linha da
  // contagem não aparece e o import fica desligado, porque a confirmação dele é
  // a frase que diz quantos contextos vão ser substituídos, e um "0" ou um
  // número por chegar ali é a decisão destrutiva tomada às cegas.
  const countKnown = contextCount !== null;
  const atCap = countKnown && contextCount >= MAX_PAGE_CONTEXTS;

  const handleExport = async () => {
    try {
      await widgetConfigApi.exportPageContexts(chatId);
    } catch (err) {
      // No err.response.data.detail here: with responseType "blob" axios hands
      // back a Blob for error bodies too, so that read is always undefined.
      toast.error(
        translate("pageContextsExportFailed") || "Could not export the page contexts"
      );
    }
  };

  // Parsed in the browser only to show the two counts in the confirmation. An
  // unreadable file fails here without spending an upload, but the file is not
  // validated here — the backend is what decides, and it reports by index.
  const acceptFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".json")) {
      toast.error(translate("pageContextsFileMustBeJson") || "File must be a .json file");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      toast.error(translate("pageContextsFileTooLarge") || "File is too large");
      return;
    }

    let incomingCount = null;
    let replacesQuickReplies = false;
    let names = [];
    let patterns = [];
    try {
      const parsed = JSON.parse(await file.text());
      if (!Array.isArray(parsed?.pageContexts)) {
        throw new Error("pageContexts missing");
      }
      incomingCount = parsed.pageContexts.length;
      // Os avisos do servidor dizem o índice no ficheiro. Com o nome, quem lê
      // reconhece o contexto sem ter de contar entradas.
      names = parsed.pageContexts.map((c) =>
        typeof c?.name === "string" && c.name.trim() ? c.name.trim() : null
      );
      // O aviso de padrão repetido traz o padrão já normalizado (sem espaços, em
      // minúsculas). Mostra-se como está escrito no ficheiro, para se encontrar.
      patterns = parsed.pageContexts.map((c) =>
        Array.isArray(c?.urlPatterns) ? c.urlPatterns.filter((x) => typeof x === "string") : []
      );
      // O ficheiro já está lido aqui, portanto não há nada a adivinhar. O
      // servidor escreve as Respostas rápidas por omissão exactamente quando a
      // chave vem e não é null (widget_config_endpoints: `if
      // parsed.welcome_quick_replies is not None`), e o export omite-a quando a
      // linha não as tem. Dizer "se o ficheiro as trouxer" era mandar o operador
      // adivinhar sobre o próprio ficheiro, na única frase que carrega uma
      // decisão destrutiva.
      replacesQuickReplies = parsed.welcomeQuickReplies != null;
    } catch {
      toast.error(
        translate("pageContextsFileUnreadable") ||
          "This file is not a page-contexts export"
      );
      return;
    }

    setPending({ file, incomingCount, replacesQuickReplies, names, patterns });
  };

  const handleConfirm = async () => {
    // Re-verificado aqui, e não só no `disabled` do botão que abre o diálogo: o
    // useModal trata o Escape e o clique fora, mas não prende o foco nem o
    // devolve. Com o diálogo aberto, o Tab sai dele para os campos de copy
    // atrás, escreve-se, o Tab volta e o Enter carrega em Substituir. O import
    // entrava, o handleImported trocava o formValues inteiro pela cópia do
    // servidor, e a edição desaparecia com um toast de sucesso por cima: é a
    // perda de dados que o bloqueio por sujo existe para evitar.
    //
    // Com aviso e não em silêncio. Um botão que não faz nada ao ser carregado
    // não se distingue de um pedido que falhou sem o dizer.
    if (hasUnsavedChanges) {
      toast.error(
        translate("pageContextsImportBlocked") ||
          "Save or discard your changes before importing"
      );
      setIsOpen(false);
      setPending(null);
      return;
    }
    setUploading(true);
    // Both panels belong to the attempt that is starting, not the one before it:
    // leaving the previous run's warnings up next to a fresh refusal describes a
    // file that is no longer the one in question.
    setErrorDetail(null);
    setWarnings(null);
    try {
      const result = await widgetConfigApi.importPageContexts(chatId, pending.file);
      // A PARTIR DAQUI o import já entrou. O que se segue pode falhar, e falhar
      // a seguir a uma escrita bem sucedida não é a mesma coisa que falhar antes
      // dela — daí os dois blocos separados em vez de um catch só.
      try {
        // Awaited, and `uploading` stays true through it: the counter and the
        // editor still show the pre-import list until the parent refetches, and
        // a success toast over stale numbers reads as a failed import.
        await onImported(chatId);
      } catch {
        // O pai já pôs a página em estado de erro e limpou o formulário, que é
        // o que impede o Guardar seguinte de desfazer o import. Aqui só se fecha
        // o diálogo, sem toast: anunciar "importado" por cima de um ecrã de erro
        // seria pior do que não anunciar nada, e anunciar falha seria falso.
        setIsOpen(false);
        setPending(null);
        return;
      }
      setWarnings({
        items: result.warnings || [],
        names: pending.names || [],
        patterns: pending.patterns || [],
      });
      setIsOpen(false);
      setPending(null);
      toast.success(
        translate("pageContextsImported", { count: result.imported_count }) ||
          `Imported ${result.imported_count} page contexts`
      );
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Not a toast: the detail is one line per bad entry, and a file with a
      // dozen mistakes is a dozen lines that a toast truncates and then hides.
      setErrorDetail(
        typeof detail === "string"
          ? detail
          : translate("pageContextsImportFailed") || "Import failed"
      );
      setIsOpen(false);
      setPending(null);
    } finally {
      setUploading(false);
    }
  };

  // Um aviso só vale se quem o lê perceber o que se passou. Por isso nomeia os
  // contextos pelo nome que têm no ficheiro, junta numa linha os que dizem a
  // mesma coisa, e não mostra códigos que não sabe explicar: um código cru no
  // ecrã confunde mais do que não dizer nada.
  const warningLines = ({ items, names, patterns }) => {
    // Sem fallback inline: o t() devolve a própria chave quando falta.
    const repeated = (name) => names.filter((n) => n === name).length > 1;
    const label = (i) => {
      if (!names[i]) return translate("pageContextsFileEntryLabel", { index: i + 1 });
      // Dois contextos com o mesmo nome não se distinguiam no aviso.
      if (repeated(names[i])) return translate("pageContextsNamedWithEntry", { name: names[i], index: i + 1 });
      return translate("pageContextsNamed", { name: names[i] });
    };
    const list = (indexes) => {
      const shown = indexes.slice(0, 5).map(label);
      const rest = indexes.length - shown.length;
      if (rest > 0) return translate("pageContextsListAndMore", { list: shown.join(", "), count: rest });
      if (shown.length < 2) return shown.join("");
      return shown.slice(0, -1).join(", ") + translate("pageContextsListAnd") + shown[shown.length - 1];
    };

    const inert = items.filter((w) => w.code === "inert_context").map((w) => w.index);
    const lines = [];
    let inertShown = false;
    // Pela ordem do servidor. A linha dos inertes junta-os todos e fica onde
    // aparece o primeiro.
    for (const warning of items) {
      switch (warning.code) {
        case "inert_context":
          if (!inertShown) {
            inertShown = true;
            lines.push(
              translate(inert.length === 1 ? "pageContextsWarningInertOne" : "pageContextsWarningInert", {
                contexts: list(inert),
              })
            );
          }
          break;
        case "reordered": {
          const indexes = warning.indexes || [];
          lines.push(
            translate(indexes.length === 1 ? "pageContextsWarningReorderedOne" : "pageContextsWarningReordered", {
              count: indexes.length,
              contexts: list(indexes),
            })
          );
          break;
        }
        case "catch_all_shadows":
          lines.push(
            translate(warning.shadowed === 1 ? "pageContextsWarningCatchAllOne" : "pageContextsWarningCatchAll", {
              context: label(warning.index),
              shadowed: warning.shadowed,
            })
          );
          break;
        case "duplicate_pattern": {
          // Um contexto sem respostas nunca aparece, portanto não conta para o
          // repetido: sem este filtro, "o de cima" podia ser um que o widget salta.
          const indexes = (warning.indexes || []).filter((i) => !inert.includes(i));
          if (indexes.length < 2) break;
          const written =
            (patterns[indexes[0]] || []).find((p) => p.trim().toLowerCase() === warning.pattern) ||
            warning.pattern;
          lines.push(translate("pageContextsWarningDuplicate", { pattern: written, contexts: list(indexes) }));
          break;
        }
        case "chat_id_mismatch":
          lines.push(translate("pageContextsWarningChatMismatch"));
          break;
        default:
          break;
      }
    }
    return lines;
  };

  const shownWarnings = warnings ? warningLines(warnings) : [];

  return (
    <div className="space-y-2 mb-3">
      {/* The label sits on this row rather than above it so the two controls
          line up with the section heading, the way the Files toolbar does.
          Owning the label here is what lets them share one flex row. */}
      <div className="flex items-center justify-between gap-3">
        <label
          className="block text-xs font-medium uppercase tracking-wide"
          style={{ color: "var(--color-text-primary)" }}
        >
          {label}
        </label>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleExport}
            title={
              translate("pageContextsExportHint") ||
              "Exports what is saved, not unsaved edits"
            }
            className={IO_BUTTON}
          >
            <Download size={14} />
            {translate("pageContextsExport") || "Export"}
          </button>

          <button
            type="button"
            onClick={() => setIsOpen(true)}
            disabled={hasUnsavedChanges || !countKnown}
            // No title for the blocked case: browsers do not deliver pointer
            // events to a disabled control, so its native tooltip never opens
            // and the reason would never reach anyone. It is rendered as text
            // below instead.
            title={
              hasUnsavedChanges
                ? undefined
                : translate("pageContextsImportHint") ||
                  "Replaces every page context of this chat"
            }
            className={`${IO_BUTTON} disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            <Upload size={14} />
            {translate("pageContextsImport") || "Import"}
          </button>
        </div>
      </div>

      {countKnown && (
        <span
          className="block text-xs"
          style={{ color: atCap ? "var(--color-warning)" : "var(--color-text-muted)" }}
        >
          {/* Sem "/ máximo" no caso normal: o tecto que costuma apertar primeiro
              é o MAX_TOTAL_URL_PATTERNS, e nada aqui conta padrões. Com os tectos
              da RPAA-246 (500 e 1000), um chat com 210 contextos a 5 padrões lia
              "210 / 500", aparentemente a menos de metade, enquanto todos os
              Guardar já eram recusados com 422 e todos os imports com 400. Um
              número sozinho não afirma folga nenhuma.

              No tecto é outra coisa: aí a contagem já é o limite, e dizê-lo custa
              uma string. Sem ela, o MAX_PAGE_CONTEXTS existia só para trocar a cor
              do texto, e a cor sozinha não é sinal nenhum para quem não a
              distingue nem para quem corre em alto contraste. */}
          {atCap
            ? translate("pageContextsCountAtCap", { count: contextCount }) ||
              `${contextCount} page contexts: the maximum this chat allows`
            : translate("pageContextsCountPlain", { count: contextCount }) ||
              `${contextCount} page contexts`}
        </span>
      )}

      {/* Visible, not a tooltip on the disabled button: this line is already the
          "can I bulk-import right now" summary, and a greyed control with no
          stated reason is the state decision 6 exists to explain. */}
      {hasUnsavedChanges && (
        <span className="block text-xs" style={{ color: "var(--color-warning)" }}>
          {translate("pageContextsImportBlocked") ||
            "Save or discard your changes before importing"}
        </span>
      )}

      {errorDetail && (
        <div
          className="rounded-lg p-3 text-xs"
          style={{
            border: "1px solid var(--color-error-border)",
            color: "var(--color-text-primary)",
          }}
        >
          <div className="flex items-start justify-between gap-2 mb-1">
            <span className="font-medium">
              {translate("pageContextsImportRefused") || "The file was refused"}
            </span>
            <button type="button" onClick={() => setErrorDetail(null)} aria-label="Close">
              <X size={14} />
            </button>
          </div>
          <pre className="whitespace-pre-wrap overflow-auto" style={{ maxHeight: 200 }}>
            {describeFileEntries(errorDetail, translate)}
          </pre>
        </div>
      )}

      {/* Persistent on purpose. A list of warnings that dismisses itself after
          four seconds is a list nobody reads. It goes on the X, or when the
          chat changes — CopySection is keyed on the chat and remounts. */}
      {shownWarnings.length > 0 && (
        <div
          className="rounded-lg p-3 text-xs"
          style={{
            border: "1px solid var(--color-border)",
            backgroundColor: "var(--color-surface-raised)",
          }}
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <span className="inline-flex items-center gap-1 font-medium">
              <AlertTriangle size={14} style={{ color: "var(--color-warning)" }} />
              {translate("pageContextsWarningsTitle") || "Check these before you rely on it"}
            </span>
            <button type="button" onClick={() => setWarnings(null)} aria-label="Close">
              <X size={14} />
            </button>
          </div>
          <ul className="space-y-1 list-disc pl-4" style={{ color: "var(--color-text-secondary)" }}>
            {shownWarnings.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </div>
      )}

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="page-contexts-import-title"
            className="rounded-xl shadow-xl max-w-lg w-full mx-4"
            style={{ backgroundColor: "var(--color-surface)" }}
          >
            <div className="p-5" style={{ borderBottom: "1px solid var(--color-border)" }}>
              <div className="flex items-center justify-between">
                <h2
                  id="page-contexts-import-title"
                  className="text-lg font-semibold"
                  style={{ color: "var(--color-text-primary)" }}
                >
                  {translate("pageContextsImportTitle") || "Import page contexts"}
                </h2>
                <button
                  type="button"
                  onClick={close}
                  disabled={uploading}
                  aria-label={translate("cancel") || "Cancel"}
                  className="disabled:opacity-50"
                >
                  <X size={20} style={{ color: "var(--color-text-muted)" }} />
                </button>
              </div>
            </div>

            <div className="p-5">
              {pending ? (
                <div className="space-y-4">
                  <p style={{ color: "var(--color-text-primary)" }}>
                    {/* Sem fallback inline: o t() devolve a própria chave
                        quando ela falta, portanto o `||` nunca chegava a correr,
                        e duplicar a frase agora custava duas cópias em vez de
                        uma. */}
                    {translate(
                      pending.replacesQuickReplies
                        ? "pageContextsImportConfirmWithReplies"
                        : "pageContextsImportConfirm",
                      { current: contextCount, incoming: pending.incomingCount }
                    )}
                  </p>
                  <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                    {pending.file.name}
                  </p>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setPending(null)}
                      disabled={uploading}
                      className="px-3 py-1.5 text-sm rounded disabled:opacity-50"
                      style={{ color: "var(--color-text-secondary)" }}
                    >
                      {translate("cancel") || "Cancel"}
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirm}
                      disabled={uploading}
                      className="px-3 py-1.5 text-sm rounded disabled:opacity-50"
                      style={{
                        backgroundColor: "var(--color-mint)",
                        color: "var(--color-on-mint)",
                      }}
                    >
                      {uploading
                        ? translate("pageContextsImporting") || "Importing..."
                        : translate("pageContextsImportReplace") || "Replace"}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
                    isDragOver
                      ? "border-[var(--color-mint)] bg-[var(--color-mint-muted)]"
                      : "border-[var(--color-border)] hover:border-[var(--color-mint)]"
                  }`}
                  role="button"
                  tabIndex={0}
                  onClick={() => inputRef.current?.click()}
                  // The file input is display:none, so it is not focusable and
                  // this div is the only route to the picker. Without these a
                  // keyboard user reaches the modal and cannot choose a file.
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      inputRef.current?.click();
                    }
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragOver(true);
                  }}
                  // dragleave bubbles from the icon and the text inside, which
                  // would toggle the highlight off and straight back on.
                  onDragLeave={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragOver(false);
                    acceptFile(e.dataTransfer.files?.[0]);
                  }}
                >
                  <Upload
                    className="mx-auto mb-3"
                    size={36}
                    style={{ color: "var(--color-text-muted)" }}
                  />
                  <p className="text-sm font-medium mb-1" style={{ color: "var(--color-text-primary)" }}>
                    {translate("pageContextsDragDrop") ||
                      "Drag a JSON file here, or click to choose one"}
                  </p>
                  <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                    {translate("pageContextsImportHint") ||
                      "Replaces every page context of this chat"}
                  </p>
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".json,application/json"
                    className="hidden"
                    onChange={(e) => {
                      acceptFile(e.target.files?.[0]);
                      e.target.value = "";
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageContextsIO;
